import { ITableHeaderComponentProps } from "@/interfaces/Table";
import {
  faEllipsisV,
  faEye,
  faFilter,
  faPlus,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ElementRef, FC, useRef } from "react";
import {
  Badge,
  Button,
  Col,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";

import IconButton from "@/components/atoms/IconButton";
import { Column } from "@tanstack/react-table";
import classNames from "classnames";
import StoreCompanyModal from "../modals/company/StoreCompanyModal";
import DispatchCompaniesModal from "../modals/company/DispatchCompaniesModal";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";

const AllCompaniesHeader: FC<ITableHeaderComponentProps> = ({
  onQueryChange,
  onShowFilters,
  notHiddenColumns,
  onColumnVisibilityChange: onColumnVisibilityChangeProp,
  triggerEnableOrdering,
  resetColumnOrder,
  smallFilters,
  activeFiltersCount = 0,
}) => {
  const user = useSelector(selectCurrentUser);
  const StoreCompanyModalRef =
    useRef<ElementRef<typeof StoreCompanyModal>>(null);
  const DispatchCompaniesModalRef =
    useRef<ElementRef<typeof DispatchCompaniesModal>>(null);

  const openCreateModal = () =>
    StoreCompanyModalRef.current?.showOrHiddenModal();

  const openDispatchModal = () =>
    DispatchCompaniesModalRef.current?.showOrHiddenModal();

  const onColumnVisibilityChange = (column: Column<any, unknown>) => {
    onColumnVisibilityChangeProp?.(column.id, !column.getIsVisible());
    return column.toggleVisibility();
  };

  return (
    <div className="d-lg-flex justify-content-between">
      <Row className="flex-between-center gy-2 px-x1">
        <StoreCompanyModal ref={StoreCompanyModalRef} />
        {checkIfUserHasRoles(user, ["GENERALMANAGER"]) && (
          <DispatchCompaniesModal ref={DispatchCompaniesModalRef} />
        )}
        <Col xs="auto" className="pe-0">
          <h6 className="mb-0">Entreprises</h6>
        </Col>
        <Col xs="auto">
          <InputGroup className="position-relative input-search-width">
            <FormControl
              size="sm"
              id="search"
              type="search"
              className="shadow-none"
              placeholder="Rechercher"
              onChange={(e) => onQueryChange(e.target.value)}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              className="border-300 hover-border-secondary"
            >
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup>
        </Col>
      </Row>
      <div className="border-bottom border-200 my-3"></div>
      <div className="d-flex align-items-center justify-content-between justify-content-lg-end px-x1">
        {!!onShowFilters && (
          <>
            <div className="position-relative">
              <IconButton
                variant="nolina-default"
                size="sm"
                icon={faFilter}
                transform="shrink-4"
                iconAlign="middle"
                onClick={onShowFilters}
                className={classNames({
                  "d-xl-none": !smallFilters,
                })}
              >
                <span
                  className={classNames("ms-1", {
                    "d-none d-sm-inline-block": !smallFilters,
                  })}
                >
                  Filtres
                </span>
              </IconButton>
              {activeFiltersCount > 0 && (
                <Badge
                  bg="danger"
                  pill
                  className="position-absolute"
                  style={{
                    top: "-8px",
                    right: "-8px",
                    fontSize: "0.65rem",
                    minWidth: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </div>
            <div
              className={classNames("bg-300 mx-3", {
                "d-none d-lg-block d-xl-none": !smallFilters,
                "d-none d-lg-block": smallFilters,
              })}
              style={{ width: "1px", height: "29px" }}
            ></div>
          </>
        )}

        <div id="orders-actions">
          <IconButton
            variant="nolina-default"
            size="sm"
            icon={faPlus}
            transform="shrink-3"
            className="mx-2"
            iconAlign="middle"
            onClick={openCreateModal}
          >
            <span className="d-none d-sm-inline-block d-xl-none d-xxl-inline-block ms-1">
              Nouveau
            </span>
          </IconButton>
          <Dropdown
            align="end"
            className="btn-reveal-trigger d-inline-block me-2"
          >
            <Dropdown.Toggle split variant="nolina-default" size="sm">
              <FontAwesomeIcon icon={faEye} className="fs--2" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="border py-0">
              <div className="p-2 pb-0" style={{ width: 250 }}>
                <h6 className="dropdown-header px-0 mb-2">Mes colonnes</h6>
                {notHiddenColumns.map((column) => (
                  <Form.Check
                    key={column.id}
                    type="switch"
                    className="text-dark"
                    label={column.columnDef.header as string}
                    {...{
                      checked: column.getIsVisible(),
                      onChange: () => onColumnVisibilityChange(column),
                    }}
                  />
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown align="end" className="btn-reveal-trigger d-inline-block">
            <Dropdown.Toggle split variant="nolina-default" size="sm">
              <FontAwesomeIcon icon={faEllipsisV} className="fs--2" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="border py-0">
              <div className="py-2">
                {checkIfUserHasRoles(user, ["GENERALMANAGER"]) && (
                  <Dropdown.Item onClick={openDispatchModal}>
                    Dispatcher les entreprises
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={triggerEnableOrdering}>
                  Changer l'ordre des colonnes
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={resetColumnOrder}
                >
                  Réinitialiser l'ordre des colonnes
                </Dropdown.Item>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default AllCompaniesHeader;
